import { codigoDeRecuperacaoSchema, codigoTotpSchema, TOTP_DIGITOS } from '@locaveri/shared'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { KeyRound, LoaderCircle, Smartphone } from 'lucide-react'
import { type ReactElement, useState } from 'react'

import { CampoDeCodigo } from '@/components/auth/campo-de-codigo'
import { ContagemRegressiva, useSegundosRestantes } from '@/components/auth/contagem-regressiva'
import { AuthLayout } from '@/components/auth/auth-layout'
import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { responderDesafio2fa } from '@/features/auth/api'
import { errorMessage } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Segunda etapa do login: o código do autenticador.
 *
 * O `desafioId` fica no store (memória) e some no F5 — de propósito. Ele é a
 * prova de que a senha já foi conferida há menos de 5 minutos; persistir isso
 * em disco seria guardar meio login numa máquina compartilhada.
 *
 * O código de RECUPERAÇÃO existe porque celular quebra, é roubado e é trocado.
 * Sem essa saída, perder o aparelho significa perder o acesso ao painel da
 * operação — e alguém acabaria desligando o 2FA "só por enquanto".
 */

type Modo = 'autenticador' | 'recuperacao'

export function Desafio2faPage(): ReactElement {
  const navigate = useNavigate()
  const desafio = useAuthStore((estado) => estado.desafio)
  const definirSessao = useAuthStore((estado) => estado.definirSessao)
  const limparDesafio = useAuthStore((estado) => estado.limparDesafio)

  const [modo, setModo] = useState<Modo>('autenticador')
  const [codigo, setCodigo] = useState('')
  const [codigoDeRecuperacao, setCodigoDeRecuperacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const segundos = useSegundosRestantes(desafio?.expiraEm ?? null)
  const expirado = desafio === null || segundos === 0

  const verificar = useMutation({
    mutationFn: responderDesafio2fa,
    onSuccess: (sessao) => {
      definirSessao(sessao)
      void navigate({ to: sessao.usuario.precisaConfigurarTotp ? '/2fa/configurar' : '/' })
    },
    onError: (causa: unknown) => {
      setErro(errorMessage(causa))
      setCodigo('')
    },
  })

  function enviar(valorDoCodigo?: string): void {
    if (desafio === null || expirado) {
      return
    }

    setErro(null)

    if (modo === 'autenticador') {
      const validado = codigoTotpSchema.safeParse(valorDoCodigo ?? codigo)

      if (!validado.success) {
        setErro(`Digite os ${String(TOTP_DIGITOS)} dígitos do aplicativo autenticador.`)
        return
      }

      verificar.mutate({ desafioId: desafio.desafioId, codigoTotp: validado.data })
      return
    }

    const validado = codigoDeRecuperacaoSchema.safeParse(codigoDeRecuperacao)

    if (!validado.success) {
      setErro('Código de recuperação inválido. Ele tem 10 caracteres, sem o hífen.')
      return
    }

    verificar.mutate({ desafioId: desafio.desafioId, codigoDeRecuperacao: validado.data })
  }

  function trocarModo(): void {
    setErro(null)
    setCodigo('')
    setCodigoDeRecuperacao('')
    setModo((atual) => (atual === 'autenticador' ? 'recuperacao' : 'autenticador'))
  }

  const ocupado = verificar.isPending

  return (
    <AuthLayout
      titulo="Verificação em duas etapas"
      descricao={
        modo === 'autenticador'
          ? 'Digite o código de 6 dígitos que aparece no seu aplicativo autenticador.'
          : 'Use um dos códigos de recuperação que você guardou ao configurar o 2FA.'
      }
      rodape={
        <Link
          to="/login"
          onClick={limparDesafio}
          className="text-brand text-sm underline-offset-4 hover:underline"
        >
          Voltar para o login
        </Link>
      }
    >
      {desafio === null ? (
        <Alert variant="warning">
          <AlertDescription>
            Não há um desafio em andamento. Entre com e-mail e senha para receber um novo.
          </AlertDescription>
        </Alert>
      ) : (
        <form
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault()
            enviar()
          }}
          className="space-y-4"
        >
          {erro === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}

          {modo === 'autenticador' ? (
            <div className="space-y-2">
              <CampoDeCodigo
                rotulo="Código do aplicativo autenticador"
                valor={codigo}
                aoMudar={setCodigo}
                aoCompletar={(completo) => {
                  // Código completo é intenção de enviar: pedir um clique a mais
                  // depois de digitar 6 dígitos é atrito puro.
                  enviar(completo)
                }}
                quantidade={TOTP_DIGITOS}
                desabilitado={ocupado || expirado}
                invalido={erro !== null}
                autoFoco
              />
              <ContagemRegressiva segundos={segundos} />
            </div>
          ) : (
            <Campo rotulo="Código de recuperação" obrigatorio dica="10 caracteres, sem o hífen.">
              {(campo) => (
                <Input
                  {...campo}
                  value={codigoDeRecuperacao}
                  onChange={(evento) => {
                    setCodigoDeRecuperacao(evento.target.value.toUpperCase())
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="XXXXXXXXXX"
                  className="tabular-money uppercase"
                  disabled={ocupado || expirado}
                />
              )}
            </Campo>
          )}

          <Button type="submit" className="w-full" disabled={ocupado || expirado}>
            {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            {ocupado ? 'Verificando…' : 'Verificar código'}
          </Button>

          <Button variant="ghost" className="w-full" onClick={trocarModo} disabled={ocupado}>
            {modo === 'autenticador' ? (
              <>
                <KeyRound aria-hidden="true" />
                Usar um código de recuperação
              </>
            ) : (
              <>
                <Smartphone aria-hidden="true" />
                Usar o aplicativo autenticador
              </>
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
